// NewsletterManager.js - Component for sending newsletters with React Query caching
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNewsletterStats, useSubscribers } from '../hooks/AdminDashboardQueries';
import {
    Tooltip, Legend,
    PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import EmojiPicker from 'emoji-picker-react';
import AlertMessage from '../../../components/AlertMessage'; // Adjust path as needed
import LoadingWidget from '../../../components/LoadingWidget'; // Adjust path as needed

// Quill toolbar configuration
const quillModules = {
    toolbar: {
        container: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            ['link'],
            ['clean'],
        ],
    },
};

const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'indent',
    'align', 'link',
];

const NewsletterManager = () => {
    const [selectedSubscribers, setSelectedSubscribers] = useState([]);
    const [newsletterData, setNewsletterData] = useState({
        subject: '',
        content: '',
        send_to_all: true,
        specific_emails: []
    });
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const quillRef = useRef(null);
    const fileInputRef = useRef(null);

    const [alert, setAlert] = useState({
        show: false,
        type: 'success',
        title: '',
        message: '',
        duration: 5000
    });

    const queryClient = useQueryClient();
    const API_BASE_URL = process.env.REACT_APP_API_URL;

    // Helper function to show alerts
    const showAlert = (type, title, message, duration = 3000) => {
        setAlert({
            show: true,
            type,
            title,
            message,
            duration
        });
    };

    // React Query for fetching newsletter stats
    const {
        data: stats = {
            total_subscribers: 0,
            active_subscribers: 0,
            inactive_subscribers: 0,
            recent_subscriptions: 0,
            subscription_rate: 0
        },
        isLoading: statsLoading,
        refetch: refetchStats
    } = useNewsletterStats(API_BASE_URL);

    // React Query for fetching subscribers
    const {
        data: subscribers = [],
        isLoading: subscribersLoading,
        refetch: refetchSubscribers
    } = useSubscribers(API_BASE_URL);
    const sendNewsletterMutation = useMutation({
        mutationFn: async (newsletterData) => {
            const token = localStorage.getItem('authToken');

            const formData = new FormData();
            formData.append('subject', newsletterData.subject);
            formData.append('content', newsletterData.content);
            formData.append('send_to_all', newsletterData.send_to_all);
            const emails = newsletterData.send_to_all ? [] : selectedSubscribers;
            emails.forEach(email => formData.append('specific_emails', email));
            attachedFiles.forEach((file) => {
                formData.append('attachments', file);
            });

            const response = await axios.post(
                `${API_BASE_URL}/newsletter/send/`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            return response.data;
        },
        onMutate: () => {
            // Return context for potential rollback
            return { previousNewsletterData: newsletterData };
        },
        onSuccess: (data) => {
            showAlert(
                'success',
                'Newsletter Sent Successfully',
                `Newsletter sent to ${data.sent_count} recipients. ${data.failed_count > 0 ? `${data.failed_count} failed.` : 'All emails delivered successfully.'}`,
                3000
            );

            // Reset form
            setNewsletterData({
                subject: '',
                content: '',
                send_to_all: true,
                specific_emails: []
            });
            setSelectedSubscribers([]);
            setAttachedFiles([]);

            // Invalidate and refetch stats and subscribers to get updated counts
            queryClient.invalidateQueries(['newsletterStats']);
            queryClient.invalidateQueries(['newsletterSubscribers']);
        },
        onError: (error, variables, context) => {
            showAlert(
                'error',
                'Newsletter Sending Failed',
                error.response?.data?.message || 'Failed to send newsletter. Please check your connection and try again.',
                3000
            );
        }
    });

    // Handle newsletter form submission
    const handleSendNewsletter = async (e) => {
        e.preventDefault();
        sendNewsletterMutation.mutate(newsletterData);
    };

    // Handle subscriber selection
    const handleSelectSubscriber = (email) => {
        if (selectedSubscribers.includes(email)) {
            setSelectedSubscribers(selectedSubscribers.filter(e => e !== email));
        } else {
            setSelectedSubscribers([...selectedSubscribers, email]);
        }
    };

    // Handle file attachment
    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setAttachedFiles(prev => {
            const existing = new Set(prev.map(f => f.name + f.size));
            const deduplicated = newFiles.filter(f => !existing.has(f.name + f.size));
            return [...prev, ...deduplicated];
        });
        // Reset input so the same file can be re-attached after removal
        e.target.value = '';
    };

    const handleRemoveFile = (index) => {
        setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Generate preview with the template
    const generatePreview = async () => {
        if (!newsletterData.content.replace(/<[^>]*>/g, '').trim() || !newsletterData.subject) {
            showAlert('warning', 'Preview Not Available', 'Please enter both subject and content to preview.', 3000);
            return;
        }

        const logoModule = await import('../../../images/AE-Hero.svg');
        const logoAssetUrl = logoModule.default;

        let logoBlobUrl = '';
        try {
            const res = await fetch(logoAssetUrl);
            const blob = await res.blob();
            logoBlobUrl = URL.createObjectURL(blob);
        } catch (_) { }

        // Each logo is an <img> inside a circle div.
        // overflow:hidden on the div clips the image into a perfect circle
        // without any padding or object-fit distortion.
        const headerLogo = logoBlobUrl
            ? `<div class="logo-circle logo-circle--header"><img src="${logoBlobUrl}" alt="Alternative Energy" class="logo-img"></div>`
            : `<span class="logo-text">Alternative Energy</span>`;
        const footerLogo = logoBlobUrl
            ? `<div class="logo-circle logo-circle--footer"><img src="${logoBlobUrl}" alt="Alternative Energy" class="logo-img"></div>`
            : `<span class="logo-text">Alternative Energy</span>`;

        const formattedContent = newsletterData.content;
        const year = new Date().getFullYear();

        const previewHTML = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${newsletterData.subject} — Preview</title>
    <link href="https://fonts.googleapis.com/css2?family=Marcellus&family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet">
    <style>
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body {
            font-family: 'Open Sans', Arial, sans-serif;
            font-size: 15px;
            line-height: 1.7;
            color: #212529;
            background-color: #edf2f7;
            word-wrap: break-word;
            overflow-wrap: break-word;
            padding: 32px 16px;
        }

        /* ── Preview banner ── */
        .preview-banner {
            max-width: 620px;
            width: 100%;
            margin: 0 auto 20px;
            background: #ebf8ff;
            border: 1px solid #bee3f8;
            border-left: 4px solid #2c5282;
            border-radius: 6px;
            padding: 12px 18px;
            font-size: 13px;
            color: #2b6cb0;
            display: flex;
            align-items: flex-start;
            gap: 10px;
        }
        .preview-banner strong { font-weight: 700; }

        /* ── Card ── */
        .email-wrapper {
            max-width: 620px;
            width: 100%;
            margin: 0 auto;
            background: #ffffff;
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 4px 24px rgba(26,54,93,0.13);
            word-wrap: break-word;
            overflow-wrap: break-word;
        }

        /* ── Header ── */
        .email-header {
            background: linear-gradient(135deg, #1a365d 0%, #2c5282 60%, #4a7dbf 100%);
            padding: 36px 40px 28px;
            text-align: center;
            position: relative;
        }
        .email-header::after {
            content: '';
            display: block;
            position: absolute;
            bottom: 0; left: 0; right: 0;
            height: 4px;
            background: linear-gradient(90deg, #f6ad55, #f6e05e, #68d391, #63b3ed, #f6ad55);
        }
        .email-header .logo-wrap {
            margin-bottom: 18px;
            display: flex;
            justify-content: center;
        }
        .email-header h1 {
            font-family: 'Marcellus', Georgia, serif;
            font-size: 22px;
            font-weight: 400;
            color: #ffffff;
            line-height: 1.35;
            word-wrap: break-word;
            overflow-wrap: break-word;
            margin: 0;
        }
        .email-header .from-line {
            margin-top: 8px;
            font-size: 12px;
            color: rgba(255,255,255,0.75);
            letter-spacing: 0.5px;
            text-transform: uppercase;
        }
        .email-header .logo-text {
            font-family: 'Marcellus', Georgia, serif;
            font-size: 22px;
            color: #ffffff;
        }

        /* ── Shared logo circle ── */
        .logo-circle {
            border-radius: 50%;
            overflow: hidden;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }
        .logo-circle--header {
            width: 80px;
            height: 80px;
            background: #ffffff;
            box-shadow: 0 2px 12px rgba(0,0,0,0.18);
        }
        .logo-circle--footer {
            width: 80px;
            height: 80px;
            background: #ffffff;
            box-shadow: 0 2px 12px rgba(0,0,0,0.18);
        }
        .logo-img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
        }

        /* ── Body ── */
        .email-body { padding: 36px 40px; background: #ffffff; }
        .email-body h1, .email-body h2, .email-body h3 {
            font-family: 'Marcellus', Georgia, serif;
            color: #1a365d;
            margin: 24px 0 10px;
            line-height: 1.3;
        }
        .email-body h1 { font-size: 24px; }
        .email-body h2 { font-size: 20px; }
        .email-body h3 { font-size: 17px; padding-bottom: 6px; border-bottom: 2px solid #e2e8f0; }
        .email-body p  { margin-bottom: 14px; word-wrap: break-word; overflow-wrap: break-word; }
        .email-body ul, .email-body ol { padding-left: 22px; margin-bottom: 14px; }
        .email-body li { margin-bottom: 6px; }
        .email-body strong { color: #1a365d; }
        .email-body a { color: #2c5282; text-decoration: underline; }

        /* ── Support callout ── */
        .support-block {
            background: #ebf8ff;
            border-left: 4px solid #2c5282;
            border-radius: 0 6px 6px 0;
            padding: 14px 18px;
            margin: 28px 0 0;
            font-size: 14px;
            color: #2b6cb0;
        }
        .support-block strong { color: #1a365d; }

        /* ── Divider ── */
        .email-divider { border: none; border-top: 1px solid #e2e8f0; margin: 0; }

        /* ── Footer ── */
        .email-footer { background: #f7fafc; padding: 24px 40px; text-align: center; }
        .email-footer .footer-logo {
            margin-bottom: 14px;
            display: flex;
            justify-content: center;
        }
        .email-footer p { font-size: 12px; color: #6b7280; margin-bottom: 6px; line-height: 1.6; }
        .email-footer a { color: #2c5282; text-decoration: underline; }
        .email-footer .copyright {
            margin-top: 14px;
            padding-top: 12px;
            border-top: 1px solid #e2e8f0;
            font-size: 11px;
            color: #9ca3af;
        }
        .email-footer .logo-text { font-family: 'Marcellus', Georgia, serif; font-size: 15px; color: #6b7280; }
        .email-footer .preview-note { margin-top: 8px; font-size: 10px; color: #b0bec5; font-style: italic; }

        @media only screen and (max-width: 640px) {
            body { padding: 16px 8px; }
            .email-header { padding: 28px 24px 22px; }
            .email-body   { padding: 28px 24px; }
            .email-footer { padding: 20px 24px; }
        }
    </style>
</head>
<body>

    <div class="preview-banner">
        <span>&#128231;</span>
        <span><strong>Preview Mode &mdash;</strong> This is how your newsletter will appear to subscribers. Actual emails will include personalised unsubscribe links.</span>
    </div>

    <div class="email-wrapper">

        <div class="email-header">
            <div class="logo-wrap">${headerLogo}</div>
            <h1>${newsletterData.subject}</h1>
            <p class="from-line">From Alternative Energy</p>
        </div>

        <div class="email-body">
            ${formattedContent}
            <div class="support-block">
                <strong>Need assistance?</strong> Reply to this email or reach our support team at
                <a href="mailto:itoffice@alternative.co.sz">itoffice@alternative.co.sz</a>
            </div>
        </div>

        <hr class="email-divider">

        <div class="email-footer">
            <div class="footer-logo">${footerLogo}</div>
            <p>This email was sent to <strong>[subscriber@email.com]</strong> by Alternative Energy.</p>
            <p>If you no longer wish to receive these emails, <a href="#">click here to unsubscribe</a>.</p>
            <p class="copyright">&copy; ${year} Alternative Energy. All rights reserved.</p>
            <p class="preview-note">This is a preview. Actual emails will include proper unsubscribe functionality.</p>
        </div>

    </div>

</body>
</html>`;

        const previewWindow = window.open('', '_blank');
        previewWindow.document.write(previewHTML);
        previewWindow.document.close();

        if (logoBlobUrl) {
            setTimeout(() => URL.revokeObjectURL(logoBlobUrl), 5000);
        }
    };


    // Quick templates
    const quickTemplates = [
        {
            name: 'Monthly Update',
            subject: 'Monthly Solar Insights - Special Spring Offer!',
            content: `<p>Dear valued customer,</p><p><br></p><p>We're excited to share our latest updates with you!</p><p><br></p><p>🌞 <strong>Solar Energy Update:</strong></p><ul><li>New government rebates now available</li><li>25% increase in solar panel efficiency</li><li>Spring maintenance special: 15% off cleaning services</li></ul><p>⚡ <strong>Electrical Services:</strong></p><ul><li>Free home safety inspections this month</li><li>Smart home integration now available</li><li>Emergency service available 24/7</li></ul><p><br></p><p><strong>Special Offer:</strong> Book your free solar assessment this month and receive 10% off installation!</p><p><br></p><p>📅 <strong>Upcoming Events:</strong></p><ul><li>Free solar panel workshop: April 15th</li><li>Home energy audit open house: April 22nd</li></ul><p><br></p><p>💡 <strong>Tip of the Month:</strong></p><p>Did you know that cleaning your solar panels can improve efficiency by up to 20%? Schedule your maintenance today!</p><p><br></p><p>Best regards,<br>The Solar &amp; Electrical Team</p>`
        },
        {
            name: 'Promotional',
            subject: 'Limited Time Offer: 20% Off Solar Installation!',
            content: `<p>Dear valued customer,</p><p><br></p><p>We have an exciting limited-time offer for you!</p><p><br></p><p>🚀 <strong>SPECIAL PROMOTION:</strong></p><p>For this month only, receive 20% off complete solar installation packages. This includes:</p><ul><li>High-efficiency solar panels</li><li>Professional installation</li><li>5-year maintenance plan</li><li>Smart home monitoring</li></ul><p><br></p><p>💰 <strong>SAVE THOUSANDS:</strong></p><p>Government rebates combined with our discount can save you up to $5,000 on your solar investment!</p><p><br></p><p>📞 <strong>ACT NOW:</strong></p><p>This offer expires on the last day of this month. Call us at (555) 123-4567 or reply to this email to schedule your free consultation.</p><p><br></p><p>Best regards,<br>The Solar &amp; Electrical Team</p>`
        },
        {
            name: 'Maintenance Reminder',
            subject: 'Time for Your Solar Panel Maintenance Check',
            content: `<p>Hello,</p><p><br></p><p>It's been a while since your last solar panel maintenance check. Regular maintenance ensures:</p><p><br></p><p>🔧 <strong>Optimal Performance:</strong></p><ul><li>Maximum energy production</li><li>Early problem detection</li><li>Extended panel lifespan</li></ul><p><br></p><p>📊 <strong>Our Maintenance Package Includes:</strong></p><ul><li>Complete panel inspection</li><li>Cleaning and debris removal</li><li>Efficiency testing</li><li>System health report</li></ul><p><br></p><p>⏰ <strong>Schedule Today:</strong></p><p>Reply to this email or call us to schedule your maintenance visit.</p><p><br></p><p>Best regards,<br>The Solar &amp; Electrical Team</p>`
        }
    ];

    // Prepare chart data
    const chartData = [
        { name: 'Active', value: stats.active_subscribers, color: '#10B981' },
        { name: 'Inactive', value: stats.inactive_subscribers, color: '#EF4444' }
    ];

    const loading = statsLoading || subscribersLoading;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="mt-2 text-gray-600">Loading newsletter data...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Sending Overlay */}
            {sendNewsletterMutation.isPending && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-sm"></div>
                    <div className="relative transform scale-150">
                        <LoadingWidget />
                    </div>
                </div>
            )}

            {/* Alert Message */}
            {alert.show && (
                <AlertMessage
                    type={alert.type}
                    title={alert.title}
                    message={alert.message}
                    duration={alert.duration}
                    onDismiss={() => setAlert(prev => ({ ...prev, show: false }))}
                />
            )}

            <div className={`max-w-6xl mx-auto py-6 px-4 sm:px-6 lg:px-8 ${sendNewsletterMutation.isPending ? 'pointer-events-none opacity-50' : ''}`}>
                {/* Stats and Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    {/* Stats Cards */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Subscriber Statistics</h3>
                            <button
                                onClick={() => {
                                    refetchStats();
                                    refetchSubscribers();
                                }}
                                className="p-1 text-gray-500 hover:text-gray-700"
                                title="Refresh data"
                            >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-gray-600">Total Subscribers</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.total_subscribers}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Active Subscribers</p>
                                <p className="text-2xl font-bold text-green-600">{stats.active_subscribers}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Subscription Rate</p>
                                <p className="text-2xl font-bold text-blue-600">{stats.subscription_rate.toFixed(1)}%</p>
                            </div>
                        </div>
                    </div>

                    {/* Pie Chart */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Active vs Inactive</h3>
                        <div style={{ width: "100%", minHeight: 256 }}>
                            <ResponsiveContainer width="100%" height={256}>
                                <PieChart>
                                    <Pie
                                        data={chartData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {chartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Newsletter Composition Form */}
                <div className="bg-white rounded-lg shadow mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-xl font-semibold text-gray-800">Compose Newsletter</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Send newsletters to {stats.active_subscribers} active subscribers
                        </p>
                    </div>

                    <form onSubmit={handleSendNewsletter} className="p-6">
                        <div className="space-y-6">
                            {/* Recipient Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Recipients
                                </label>
                                <div className="space-y-3">
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="send_all"
                                            checked={newsletterData.send_to_all}
                                            onChange={() => setNewsletterData({ ...newsletterData, send_to_all: true })}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <label htmlFor="send_all" className="ml-2 text-sm text-gray-700">
                                            Send to all active subscribers ({stats.active_subscribers} recipients)
                                        </label>
                                    </div>

                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="send_specific"
                                            checked={!newsletterData.send_to_all}
                                            onChange={() => setNewsletterData({ ...newsletterData, send_to_all: false })}
                                            className="h-4 w-4 text-blue-600"
                                        />
                                        <label htmlFor="send_specific" className="ml-2 text-sm text-gray-700">
                                            Send to specific subscribers
                                        </label>
                                    </div>

                                    {!newsletterData.send_to_all && (
                                        <div className="ml-6 mt-2">
                                            <p className="text-sm text-gray-600 mb-2">Select subscribers:</p>
                                            <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2">
                                                {subscribers
                                                    .filter(s => s.is_active)
                                                    .map(subscriber => (
                                                        <div key={subscriber.id} className="flex items-center py-1">
                                                            <input
                                                                type="checkbox"
                                                                id={`sub-${subscriber.id}`}
                                                                checked={selectedSubscribers.includes(subscriber.email)}
                                                                onChange={() => handleSelectSubscriber(subscriber.email)}
                                                                className="h-4 w-4 text-blue-600 rounded"
                                                            />
                                                            <label htmlFor={`sub-${subscriber.id}`} className="ml-2 text-sm text-gray-700">
                                                                {subscriber.email}
                                                            </label>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Selected: {selectedSubscribers.length} subscribers
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Subject */}
                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                                    Subject *
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    value={newsletterData.subject}
                                    onChange={(e) => setNewsletterData({ ...newsletterData, subject: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Monthly Solar Insights - Special Spring Offer!"
                                    required
                                />
                            </div>

                            {/* Quick Templates */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Templates</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {quickTemplates.map((template, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => {
                                                setNewsletterData(prev => ({
                                                    ...prev,
                                                    subject: template.subject,
                                                    content: template.content
                                                }));
                                                // Force Quill to reflect the new content immediately
                                                const editor = quillRef.current?.getEditor();
                                                if (editor) {
                                                    editor.clipboard.dangerouslyPasteHTML(template.content);
                                                }
                                            }}
                                            className="text-left p-3 border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-200 transition-colors"
                                        >
                                            <div className="font-medium text-gray-800 mb-1">{template.name}</div>
                                            <div className="text-xs text-gray-600 truncate">{template.subject}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Content Editor */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                                        Newsletter Content *
                                    </label>
                                    <div className="flex items-center gap-2">
                                        {/* Attach File button */}
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                                            title="Attach file"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                            </svg>
                                            <span>Attach</span>
                                        </button>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            className="hidden"
                                            onChange={handleFileChange}
                                        />
                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                                                title="Insert emoji"
                                            >
                                                <span className="text-base">😊</span>
                                                <span>Emoji</span>
                                            </button>
                                            {showEmojiPicker && (
                                                <div className="absolute right-0 top-10 z-50 shadow-xl rounded-lg overflow-hidden">
                                                    <div
                                                        className="fixed inset-0"
                                                        onClick={() => setShowEmojiPicker(false)}
                                                    />
                                                    <div className="relative z-10">
                                                        <EmojiPicker
                                                            onEmojiClick={(emojiData) => {
                                                                const editor = quillRef.current?.getEditor();
                                                                if (editor) {
                                                                    const range = editor.getSelection(true);
                                                                    editor.insertText(range.index, emojiData.emoji);
                                                                    editor.setSelection(range.index + emojiData.emoji.length);
                                                                } else {
                                                                    setNewsletterData(prev => ({
                                                                        ...prev,
                                                                        content: prev.content + emojiData.emoji
                                                                    }));
                                                                }
                                                                setShowEmojiPicker(false);
                                                            }}
                                                            width={320}
                                                            height={400}
                                                            searchDisabled={false}
                                                            skinTonesDisabled
                                                            previewConfig={{ showPreview: false }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Attached files list */}
                                {attachedFiles.length > 0 && (
                                    <div className="mb-2 flex flex-wrap gap-2">
                                        {attachedFiles.map((file, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700"
                                            >
                                                <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                </svg>
                                                <span className="max-w-[160px] truncate">{file.name}</span>
                                                <span className="text-blue-400 shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveFile(index)}
                                                    className="ml-1 text-blue-400 hover:text-red-500 transition-colors font-bold"
                                                    title="Remove attachment"
                                                >×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                                    <ReactQuill
                                        ref={quillRef}
                                        value={newsletterData.content}
                                        onChange={(value) => setNewsletterData(prev => ({ ...prev, content: value }))}
                                        modules={quillModules}
                                        formats={quillFormats}
                                        placeholder="Write your newsletter content here..."
                                        style={{ minHeight: '320px' }}
                                        theme="snow"
                                    />
                                </div>
                                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                    <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Use the toolbar to add <strong>bold</strong>, bullet lists, headings, and more. Click the Emoji button to insert emojis anywhere in your content.
                                </div>
                            </div>

                            {/* Preview and Send */}
                            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={generatePreview}
                                        disabled={!newsletterData.content.replace(/<[^>]*>/g, '').trim() || !newsletterData.subject}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                    >
                                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        Preview Email
                                    </button>

                                </div>

                                <div className="flex space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setNewsletterData({
                                                subject: '',
                                                content: '',  // Empty content
                                                send_to_all: true,
                                                specific_emails: []
                                            });
                                            setSelectedSubscribers([]);
                                            setAttachedFiles([]);
                                            setAlert({ ...alert, show: false }); // Clear any alert
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center"
                                    >
                                        <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Clear All
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={sendNewsletterMutation.isPending || !newsletterData.subject || !newsletterData.content.replace(/<[^>]*>/g, '').trim()}
                                        className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg hover:from-green-700 hover:to-green-900 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                    >
                                        {sendNewsletterMutation.isPending ? (
                                            <>
                                                <svg className="h-5 w-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                </svg>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                </svg>
                                                Send Newsletter
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Writing Tips */}
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                    <h3 className="text-lg font-semibold text-blue-800 mb-3">Writing Tips for Better Newsletters</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded border border-blue-100">
                            <h4 className="font-medium text-blue-700 mb-2 flex items-center">
                                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Formatting Tips
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Use bullet points for lists</li>
                                <li>• Add emojis for visual appeal 🌞 ⚡</li>
                                <li>• Keep paragraphs short (2-3 sentences)</li>
                                <li>• Use clear section headers</li>
                                <li>• Include a call to action</li>
                            </ul>
                        </div>
                        <div className="bg-white p-4 rounded border border-blue-100">
                            <h4 className="font-medium text-blue-700 mb-2 flex items-center">
                                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                Content Structure
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>1. Friendly greeting</li>
                                <li>2. Main announcements</li>
                                <li>3. Special offers/promotions</li>
                                <li>4. Upcoming events</li>
                                <li>5. Helpful tip or advice</li>
                                <li>6. Professional closing</li>
                            </ul>
                        </div>
                        <div className="bg-white p-4 rounded border border-blue-100">
                            <h4 className="font-medium text-blue-700 mb-2 flex items-center">
                                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                Engagement Tips
                            </h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Ask questions to encourage replies</li>
                                <li>• Include links to your website</li>
                                <li>• Share customer success stories</li>
                                <li>• Offer exclusive subscriber deals</li>
                                <li>• Keep it conversational</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default NewsletterManager;